import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconCustomLocation,
  IconRate,
  AvatarLarge,
  PrimaryButton,
  SecondaryButton,
} from '../../components';

import recommended from './../../assets/recommended.png';
import placeholder from './../../assets/userPlaceholder.jpg';

import css from './SuggestionListingCard.module.css';
import { paymentListingTypes } from '../../config/configStripe';
import { types as sdkTypes } from '../../util/sdkLoader';
const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      <span className={css.priceLabel}>
        <FormattedMessage id="SuggestionListingCard.pricePerHour" />
      </span>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <span>{formattedPrice}</span>
      )}
    </div>
  );
};

const ListingCardImage = props => {
  const {
    currentListing,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
    />
  );
};

export const SuggestionListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    reviews = [],
  } = props;

  const { categories = [] } = config?.categoryConfiguration || {};

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price: priceRaw, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const {
    listingType,
    cardStyle,
    location: listingLocation,
    categoryLevel1,
    accumulatedReviews = 0,
    totalReviews = 0,
  } = publicData || {};

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const { address, building = '' } = listingLocation || {};
  let category = categories.find(c => c.id == categoryLevel1);

  const getAverageRating = () => {
    if (accumulatedReviews > 0 && totalReviews > 0) {
      return (accumulatedReviews / totalReviews).toFixed(1);
    }

    return 0;
  };

  const showPrice = paymentListingTypes.includes(listingType);

  const priceObj = priceRaw ? priceRaw : { amount: 0, currency: 'USD', _sdkType: 'Money' };
  const price = new Money(priceObj.amount, priceObj.currency);

  let imgSrc = listing?.images?.[0]?.attributes?.variants?.default?.url || null;
  let authorImgSrc = listing?.author?.authorImage?.attributes?.variants?.default?.url || placeholder;

  return (
    <div className={classes}>
      <div className={css.topSection}>
        <div className={css.ratingContainer}>
          <IconRate className={css.ratingIcon} />
          <span className={css.ratingValue}>{getAverageRating() || 0}</span>
          <span className={css.reviewCount}>({totalReviews || 0})</span>
        </div>
        {category && category?.name ? (
          <div className={css.categoryBadge}>{category.name}</div>
        ) : null}
        <div className={css.flag}>
          <img src={recommended} alt="recommended"/>
        </div>
      </div>

      <NamedLink className={css.imageLink} name="ListingPage" params={{ id, slug }}>
        {imgSrc ? <img src={imgSrc}/> : <ListingCardThumbnail
          // style={style}
          listingTitle={title}
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        />}
      </NamedLink>

      <div className={css.infoSection}>
        <div className={css.authorSection}>
          <div className={css.avatar}>
            <img src={authorImgSrc}/>
          </div>
          <div className={css.authorInfo}>
            <div className={css.authorName}>{title}</div>
            {showPrice && !!price ? <PriceMaybe
              price={price}
              publicData={publicData}
              config={config}
              intl={intl}
              listingTypeConfig={foundListingTypeConfig}
            /> : null}
          </div>
        </div>

        {address ? (
          <div className={css.locationSection}>
            <IconCustomLocation className={css.locationIcon} />
            <span className={css.locationText}>{[address, building].filter(a => !!a && a.trim() !== '').join(', ')}</span>
          </div>
        ) : null}

        <div className={css.buttonsSection}>
          <NamedLink
            className={css.profileButton}
            name="ListingPage"
            params={{ id, slug }}
          >
            <PrimaryButton className={css.primaryBtn}>
              <FormattedMessage id="SuggestionListingCard.viewProfile" />
            </PrimaryButton>
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

export default SuggestionListingCard;
